import { Reveal } from "@components/ui";

const consumptionTiers = [
  {
    range: "0 – 20 m³",
    price: "35.00",
    unit: "AED/m³",
    description: "9 – 10 kg (Typical apartment or studio)",
  },
  {
    range: "21 – 50 m³",
    price: "33.00",
    unit: "AED/m³",
    description: "10 – 25 kg (Small villa / 2–3 bedrooms)",
  },
  {
    range: "51 – 100 m³",
    price: "31.00",
    unit: "AED/m³",
    description: "25 – 50 kg (Large villa / family units)",
  },
  {
    range: "101 m³ and above",
    price: "30.00",
    unit: "AED/m³",
    description: "50 kg + (Bulk / community rate)",
  },
];

const notes = [
  ["All prices exclude 5% VAT", "Billed monthly based on meter readings (in m³)"],
  [
    "Includes correction factors for pressure and temperature variations (see below).",
    "Bills may vary with seasonal LPG market fluctuations and UAE fuel price index",
  ],
];

export function ResidentialTariffsSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-8 md:py-12 lg:py-[80px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-6 md:gap-8 lg:gap-10">
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase font-albert-sans">
              Tariffs
            </span>
            <h2 className="text-[24px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Residential Tariffs (Piped LPG / SNG Systems)
            </h2>
            <p className="max-w-[910px] text-[14px] md:text-base lg:text-[28px] tracking-[-0.04em] text-[#62636c] font-albert-sans">
              Transparent pricing based on your monthly consumption. The more
              you use, the lower your unit rate.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {consumptionTiers.map((tier, index) => (
            <Reveal key={tier.range} direction="up" delay={index * 80}>
              <div className="h-full rounded-[12px] md:rounded-[24px] border border-[#62636C33] bg-white hover:border-[#f24d2b]/20 hover:shadow-[0_8px_32px_rgba(242,77,43,0.10)] p-4 md:p-6 flex flex-col gap-4 md:gap-6 transition-all duration-300 group">
                <p className="text-[18px] md:text-[24px] lg:text-[28px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans">
                  {tier.range}
                </p>
                <div className="relative">
                  <div className="absolute -left-0 top-0 bottom-0 w-1 rounded-full bg-gradient-to-b from-[#f24d2b] to-[#f24d2b]/30 group-hover:opacity-100 opacity-0 transition-opacity duration-300" />
                  <div className="pl-0 group-hover:pl-4 transition-all duration-300">
                    <p className="text-[32px] md:text-[40px] lg:text-[48px] tracking-[-0.04em] font-bold text-[#f24d2b] font-albert-sans leading-[1.1]">
                      {tier.price}
                    </p>
                    <p className="text-[14px] md:text-base tracking-[-0.04em] text-[#62636c] font-albert-sans mt-0.5">
                      {tier.unit}
                    </p>
                  </div>
                </div>
                <p className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] text-[#62636c] font-albert-sans">
                  {tier.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal direction="up">
          <div className="rounded-[12px] md:rounded-[24px] bg-[#f6f6f9] p-4 md:p-6 lg:p-8 flex flex-col gap-3 md:gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-[8px] bg-[#122d63]/8 text-[#122d63] flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M12 16v-4M12 8h.01"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-[20px] md:text-[24px] lg:text-[28px] tracking-[-0.04em] font-medium text-[#62636c] font-albert-sans">
                NOTE
              </h3>
            </div>
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {notes.map((col, colIdx) => (
                <div key={colIdx} className="flex-1 flex flex-col gap-4 md:gap-6">
                  {col.map((note) => (
                    <div key={note} className="flex items-start gap-3">
                      <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#122d63]/8 flex items-center justify-center shrink-0 mt-1">
                        <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 md:w-3.5 md:h-3.5">
                          <circle cx="12" cy="12" r="10" stroke="#122d63" strokeWidth="1.5" />
                          <path
                            d="M12 16v-4M12 8h.01"
                            stroke="#122d63"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="text-[14px] md:text-base lg:text-[24px] tracking-[-0.04em] leading-[32px] text-[#62636c] font-albert-sans">
                        {note}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
